// 즐겨찾기 — 최상단 운영 메뉴 전용 페이지.
//
// 프로그램 시스템에서 분리된 단독 앱. frontend.html 은 sandbox="allow-scripts"
// iframe 안에서만 실행되며(쿠키·DOM·API 접근 불가), 호스트(인증된 이 컴포넌트)와
// postMessage 브리지(TimBox)로만 통신한다. 공유 데이터는 /favorites/data 에,
// 페이지 마크업은 /favorites/app 에 보관된다(서버 코드 실행 없음).
import { useEffect, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";

// 모든 iframe 문서 최상단에 주입 — window.TimBox 노출.
const BRIDGE = `<script>
(function(){
  var pending = {}, seq = 0;
  window.TimBox = {
    load: function(cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'load', id:id }, '*'); },
    save: function(data, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'save', id:id, data:data }, '*'); },
    aiChat: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'aiChat', id:id, payload:payload }, '*'); },
    me: function(cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'me', id:id }, '*'); }
  };
  window.addEventListener('message', function(e){
    var m = e.data; if(!m || m.__timbox !== 1) return;
    if(m.action === 'result' && pending[m.id]){ var cb = pending[m.id]; delete pending[m.id]; cb(m.data); }
  });
})();
<\/script>
`;

function isAdmin(user: any): boolean {
  const roles: string[] = user?.roles ?? user?.role_codes ?? [];
  return roles.includes("SUPER_ADMIN") || roles.includes("ADMIN");
}

const editorBox: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12, lineHeight: 1.55, padding: 12, border: "1px solid var(--border)", borderRadius: 8,
  background: "var(--bg-0)", color: "var(--text-1)", resize: "vertical", outline: "none", tabSize: 2,
};

export default function FavoritesPage() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { user } = useAuth();
  const admin = isAdmin(user);

  const [frontend, setFrontend] = useState("");
  const [dir,      setDir]      = useState("");
  const [loading,  setLoading]  = useState(true);

  const [editing,   setEditing]   = useState(false);
  const [feDraft,   setFeDraft]   = useState("");
  const [saving,    setSaving]    = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Load the app markup
  useEffect(() => {
    setLoading(true);
    api.get<{ frontend: string; dir: string }>("/favorites/app")
      .then(r => { setFrontend(r.data.frontend); setDir(r.data.dir); setFeDraft(r.data.frontend); })
      .catch(() => toast.error("즐겨찾기를 불러오지 못했습니다"))
      .finally(() => setLoading(false));
  }, []);

  // Bridge: relay requests from the sandboxed iframe.
  useEffect(() => {
    const onMsg = async (e: MessageEvent) => {
      const m = e.data;
      if (!m || m.__timbox !== 1) return;
      if (e.source !== iframeRef.current?.contentWindow) return;

      const reply = (data: unknown) =>
        iframeRef.current?.contentWindow?.postMessage({ __timbox: 1, action: "result", id: m.id, data }, "*");

      if (m.action === "load") {
        try { const r = await api.get("/favorites/data"); reply(r.data); }
        catch { reply({}); }
      } else if (m.action === "save") {
        try { const r = await api.put("/favorites/data", m.data); reply(r.data); }
        catch { reply(null); }
      } else if (m.action === "me") {
        reply({ id: user?.id ?? null, username: user?.username ?? null, name: user?.name ?? null, roles: user?.roles ?? [], admin });
      } else if (m.action === "aiChat") {
        try { const r = await api.post(`/ai/chat`, m.payload, { timeout: 120000 }); reply(r.data); }
        catch (err: any) {
          reply({ error: err?.response?.data?.error?.message || err?.response?.data?.detail || err?.message || "AI 요청 실패" });
        }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [user, admin]);

  const saveFrontend = async () => {
    setSaving(true);
    try {
      const r = await api.put<{ frontend: string }>("/favorites/app", { content: feDraft });
      setFrontend(r.data.frontend);
      setReloadKey(k => k + 1);
      toast.success("저장됨");
    } catch (e: any) { toast.error("저장 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", borderBottom: "1px solid var(--border)", flexShrink: 0, background: "var(--bg-0)" }}>
        <span style={{ fontSize: 18 }}>⭐</span>
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--text-1)", flex: 1 }}>즐겨찾기</h1>
        {admin && (
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => setEditing(false)} style={tabBtn(!editing)}>👁 미리보기</button>
            <button onClick={() => { setFeDraft(frontend); setEditing(true); }} style={tabBtn(editing)}>✏️ 편집</button>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)", fontSize: 13 }}>불러오는 중…</div>
      ) : editing && admin ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, padding: "10px 24px 16px", gap: 10 }}>
          {dir && (
            <span title="로컬 폴더 경로" style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "ui-monospace, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              📂 {dir}
            </span>
          )}
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>
            HTML/CSS/JS를 작성하세요. <code>TimBox.load(cb)</code> / <code>TimBox.save(obj)</code> 로 데이터를 읽고 쓸 수 있습니다.
          </div>
          <textarea value={feDraft} onChange={e => setFeDraft(e.target.value)} spellCheck={false} style={{ ...editorBox, flex: 1, minHeight: 200 }} />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="t-btn t-btn-primary" onClick={saveFrontend} disabled={saving || feDraft === frontend}>
              {saving ? "저장 중…" : "저장"}
            </button>
          </div>
        </div>
      ) : (
        <iframe
          key={reloadKey}
          ref={iframeRef}
          title="즐겨찾기"
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-downloads"
          srcDoc={BRIDGE + frontend}
          style={{ flex: 1, width: "100%", border: "none", background: "#fff" }}
        />
      )}
    </div>
  );
}

function tabBtn(active: boolean): React.CSSProperties {
  return {
    fontSize: 12, padding: "4px 12px", borderRadius: 6, cursor: "pointer",
    border: `1px solid ${active ? "var(--p-400)" : "var(--border)"}`,
    background: active ? "rgba(99,102,241,0.1)" : "var(--bg-0)",
    color: active ? "var(--p-600)" : "var(--text-2)", fontWeight: active ? 600 : 400,
  };
}
